import { useEffect, useState } from "react";

const DEFAULT_FALLBACK = {
  latitude: 13.0827,
  longitude: 80.2707,
  label: "Chennai (fallback)",
  source: "fallback",
};

// Shared (module-level) cache to avoid multiple prompts when multiple components mount.
const shared = {
  started: false,
  coords: null,
  status: { state: "idle", message: "" },
  listeners: new Set(),
};

function notify() {
  shared.listeners.forEach((fn) => fn({ coords: shared.coords, status: shared.status }));
}

function startRequest(fallback = DEFAULT_FALLBACK) {
  if (shared.started) return;
  shared.started = true;

  function applyFallback(reason) {
    shared.status = { state: "fallback", message: reason };
    shared.coords = fallback;
    notify();
  }

  if (!("geolocation" in navigator)) {
    applyFallback("Geolocation not available in this browser.");
    return;
  }

  shared.status = { state: "requesting", message: "Requesting device location…" };
  notify();

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      shared.status = { state: "granted", message: "Using device location." };
      shared.coords = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        label: "Device location",
        source: "geo",
      };
      notify();
    },
    (err) => {
      applyFallback(err?.message || "Location permission denied.");
    },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 }
  );
}

export default function useGeoLocation(options = {}) {
  const { fallback = DEFAULT_FALLBACK } = options;
  const [coords, setCoords] = useState(shared.coords);
  const [status, setStatus] = useState(shared.status);

  useEffect(() => {
    startRequest(fallback);
    const listener = (next) => {
      setCoords(next.coords);
      setStatus(next.status);
    };
    shared.listeners.add(listener);
    // Sync once in case cache updated between render and effect.
    listener({ coords: shared.coords, status: shared.status });
    return () => {
      shared.listeners.delete(listener);
    };
  }, [fallback]);

  return { coords, status };
}

